/*
  <ws-sticky-cta>: shows once the first section of the page has scrolled
  out of view, and hides again while the button's own target (an in-page
  anchor such as #quote-request) or the footer is on screen.

  While hidden it is aria-hidden and its link is out of the tab order, so
  keyboard and screen reader users never land on an invisible button.
*/
if (!customElements.get('ws-sticky-cta')) {
  customElements.define(
    'ws-sticky-cta',
    class WsStickyCta extends HTMLElement {
      connectedCallback() {
        if (!('IntersectionObserver' in window)) return;

        this.link = this.querySelector('a');
        this.pastTop = false;
        this.blockers = new Set();

        const firstSection = document.querySelector('#MainContent > .shopify-section:first-child');
        const footer = document.querySelector('.shopify-section-group-footer-group, footer');
        const target = this.getTarget();

        this.topObserver = new IntersectionObserver(([entry]) => {
          // Past the top only once the section has left through the top edge.
          this.pastTop = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.update();
        });

        this.blockObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.blockers.add(entry.target);
            } else {
              this.blockers.delete(entry.target);
            }
          });
          this.update();
        });

        if (firstSection && !firstSection.contains(this)) {
          this.topObserver.observe(firstSection);
        } else {
          this.pastTop = true;
        }

        [target, footer].forEach((element) => {
          if (element && !element.contains(this)) this.blockObserver.observe(element);
        });

        this.update();
      }

      disconnectedCallback() {
        if (this.topObserver) this.topObserver.disconnect();
        if (this.blockObserver) this.blockObserver.disconnect();
      }

      getTarget() {
        const href = this.link ? this.link.getAttribute('href') : '';
        if (!href) return null;
        try {
          // Accept "#id", "/this/page#id" or a full URL to this page.
          const url = new URL(href, window.location.href);
          if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return null;
          if (url.hash.length < 2) return null;
          return document.getElementById(decodeURIComponent(url.hash.slice(1)));
        } catch (error) {
          return null;
        }
      }

      update() {
        const visible = this.pastTop && this.blockers.size === 0;
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', String(!visible));
        if (this.link) this.link.tabIndex = visible ? 0 : -1;
      }
    }
  );
}
